import React, { useState, useEffect } from "react";
import axios from "axios";
import CustomNavbar from "../CustomPermissions/CustomNavbar";

import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../../config/api";

const CreateTeamLeader = () => {
  const [successMessage, setSuccessMessage] = useState("");
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  const [newTeamLeader, setNewTeamLeader] = useState({
    name: "",
    username: "",
    password: "",
    dept: "",
    image: null,
    designation: "",
  });

  const [createAsDualRole, setCreateAsDualRole] = useState(false);
  const [hrTargets, setHrTargets] = useState({
    target: 0,
    revenue: 0
  });

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewTeamLeader((prevTeamLeader) => ({
      ...prevTeamLeader,
      [name]: value,
    }));
  };

  const handleDeptChange = (event) => {
    const { value } = event.target;
    setNewTeamLeader((prevTeamLeader) => ({
      ...prevTeamLeader,
      dept: value,
    }));
  };

  const handleDesignationChange = (event) => {
    const { value } = event.target;
    setNewTeamLeader((prevTeamLeader) => ({
      ...prevTeamLeader,
      designation: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setNewTeamLeader((prevTeamLeader) => ({
      ...prevTeamLeader,
      image: file,
    }));
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    try {
      const formData = new FormData();
      formData.append("name", newTeamLeader.name);
      formData.append("username", newTeamLeader.username);
      formData.append("password", newTeamLeader.password);
      formData.append("dept", newTeamLeader.dept);
      formData.append("image", newTeamLeader.image);
      formData.append("designation", newTeamLeader.designation);

      // Step 1: Create Team Leader
      const response = await axios.post(
        getApiUrl("/LeadersTable/create_TeamLeader_api/"),
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      // Step 2: If dual role, also create HR record and assign dual permissions
      if (createAsDualRole) {
        // Get the created user ID - we need to find the user by username
        const accessToken = localStorage.getItem("access_token");

        // First, get the user ID by username
        const usersResponse = await axios.get(
          `${API_BASE_URL}/Authentication/manage/users/all/`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        const createdUser = usersResponse.data.users.find(u => u.username === newTeamLeader.username);

        if (createdUser) {
          // Assign dual role
          await axios.post(
            `${API_BASE_URL}/Authentication/manage/user/${createdUser.id}/assign-dual-role/`,
            {
              department_id: newTeamLeader.dept,
              designation_id: newTeamLeader.designation || null,
              target: hrTargets.target || 0,
              revenue: hrTargets.revenue || 0
            },
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );

          setSuccessMessage("Team Leader created successfully with Dual Role! User can now toggle between Team Leader and HR modes.");
        } else {
          setSuccessMessage("Team Leader created successfully!");
        }
      } else {
        setSuccessMessage("Team Leader created successfully!");
      }

      // Reset form
      setNewTeamLeader({
        name: "",
        username: "",
        password: "",
        dept: "",
        image: null,
        designation: "",
      });
      setCreateAsDualRole(false);
      setHrTargets({ target: 0, revenue: 0 });
    } catch (error) {
      console.error("Error saving Team Leader:", error);
      setSuccessMessage("Error creating Team Leader. Please try again.");
    }
  };

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(
          getApiUrl("/LeadersTable/teamleader-types/", false),
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );
        setDepartments(response.data);
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };

    const fetchDesignations = async () => {
      try {
        const response = await axios.get(
          getApiUrl("/designations/", false),
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );
        setDesignations(response.data);
      } catch (error) {
        console.error("Error fetching designations:", error);
      }
    };

    fetchDepartments();
    fetchDesignations();
  }, []);

  return (
    <div className=" bg-gray-100" style={{ display: 'flex' }}>
      <CustomNavbar />
      <div className="containers" style={{ width: "100%", maxHeight: '100vh', overflowX: 'scroll' }}>
        <h3 className="text-2xl font-semibold text-gray-800 mb-6" style={{ padding: "10px", background: "var(--bg-blue)", color: "white", paddingLeft: "30px" }}>Add Team Leader</h3>

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6" role="alert">
            <span className="block sm:inline">{successMessage}</span>
          </div>
        )}

        <div className=" rounded-lg p-3" style={{ padding: "10px 65px" }}>
          <form onSubmit={handleFormSubmit} encType="multipart/form-data">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={newTeamLeader.name}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">Username</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={newTeamLeader.username}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={newTeamLeader.password}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="dept" className="block text-sm font-medium text-gray-700">Department</label>
                <select
                  id="dept"
                  name="dept"
                  value={newTeamLeader.dept}
                  onChange={handleDeptChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Department</option>
                  {departments.map((type) => (
                    <option key={type.id} value={type.id}>{type.id} - {type.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="designation" className="block text-sm font-medium text-gray-700">Designation</label>
                <select
                  id="designation"
                  name="designation"
                  value={newTeamLeader.designation}
                  onChange={handleDesignationChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Designation</option>
                  {designations.map((designation) => (
                    <option key={designation.id} value={designation.id}>{designation.id} - {designation.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="image" className="block text-sm font-medium text-gray-700">Image</label>
                <input
                  type="file"
                  id="image"
                  onChange={handleImageChange}
                  name="image"
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Dual Role Option - Only accessible by superusers */}
            {localStorage.getItem("is_superuser") === "true" && (
              <div className="mt-6 p-4 bg-purple-50 border-2 border-purple-200 rounded-lg">
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="dualRole"
                    checked={createAsDualRole}
                    onChange={(e) => setCreateAsDualRole(e.target.checked)}
                    className="h-5 w-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="dualRole" className="ml-3 flex items-center cursor-pointer">
                    <span className="text-base font-semibold text-purple-700">
                      👥 Also create as HR Associate (Dual Role)
                    </span>
                  </label>
                </div>

                {createAsDualRole && (
                  <div className="ml-8 space-y-4">
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                      <p className="text-sm text-blue-700">
                        <strong>Dual Role:</strong> User will get a toggle button in their sidebar to switch between Team Leader and HR Associate modes.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Target Candidates (HR)
                        </label>
                        <input
                          type="number"
                          value={hrTargets.target}
                          onChange={(e) => setHrTargets({ ...hrTargets, target: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          min="0"
                          placeholder="e.g., 50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Target Revenue (lacs)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={hrTargets.revenue}
                          onChange={(e) => setHrTargets({ ...hrTargets, revenue: parseFloat(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          min="0"
                          placeholder="e.g., 10.5"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 text-right">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Create
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateTeamLeader;
